import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCustomerCareLogsByCustomer, removeCustomerCareLog } from "@/redux/actions/customerCareLogActions";
import { Table, Button, Popconfirm, Row, Col, Space, Tag, Empty } from "antd";
import moment from "moment";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { checkPermission } from "@/utils/permission";
import HtmlParser from "react-html-parser";
import CustomerCareForm from "@/components/customers/CustomerCareForm";

const CustomerCareList = ({ customer }) => {
    const customerId = customer?.id;

    const dispatch = useDispatch();

    const { customerCareLogList } = useSelector(
        (state) => state.customerCareLogReducer
    );

    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const allowCreate = checkPermission("customer_care_logs", "create");
    const allowUpdate = checkPermission("customer_care_logs", "update");
    const allowDelete = checkPermission("customer_care_logs", "remove");

    const loadData = async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            await dispatch(getCustomerCareLogsByCustomer({}, customerId));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [customerId]);

    const handleCreate = () => {
        setEditingItem(null);
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingItem({ ...record });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        await dispatch(removeCustomerCareLog([id]));
        loadData();
    };

    const columns = [
        {
            title: "STT",
            dataIndex: "stt",
            width: 50,
            align: "center",
            render: (text, record, index) => index + 1
        },
        {
            title: "Ngày gọi / Người gọi",
            key: "called_staff",
            width: 220,
            render: (_, record) => {
                if (!record.called_at) return null;

                const date = moment(record.called_at).format("YYYY-MM-DD");
                const time = moment(record.called_at).format("HH:mm");

                const staff = record.staff;

                return (
                    <div style={{ lineHeight: 1.25 }}>
                        <div>{date} <span style={{ color: "#999", fontSize: 12 }}>{time}</span></div>
                        {staff && (
                            <div style={{ marginTop: 3 }}>
                                <span style={{ color: "#999" }}>
                                    {staff.user_role?.name || ""}:{" "}
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                    {staff.full_name}
                                </span>
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: "Nội dung CSKH",
            dataIndex: "categories",
            width: 300,
            render: (categories) => {
                if (!categories || categories.length === 0) return null;

                return (
                    <>
                        {categories.map((c) => (
                            <Tag
                                key={c.id}
                                color="blue"
                                style={{ marginBottom: 4 }}
                            >
                                {c.name}
                            </Tag>
                        ))}
                    </>
                );
            }
        },
        {
            title: "Phản hồi khách",
            dataIndex: "customer_feedback",
            render: (text) => <>{HtmlParser(text)}</>
        },
        {
            title: "Hành động",
            width: 130,
            align: "center",
            render: (_, record) => (
                <>
                    <Button
                        type="link"
                        disabled={!allowUpdate}
                        onClick={() => handleEdit(record)}
                    >
                        Sửa
                    </Button>

                    <Popconfirm
                        title="Bạn có chắc muốn xoá bản ghi này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Xoá"
                        cancelText="Hủy"
                        placement="left"
                        disabled={!allowDelete}
                    >
                        <Button disabled={!allowDelete} danger type="link">
                            Xoá
                        </Button>
                    </Popconfirm>
                </>
            )
        }
    ];

    return (
        <div>
            <div className="mt-4 mb-4">
                <Row gutter={8}>
                    <Col>
                        <Space size={8}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                                disabled={!allowCreate}
                            >
                                Thêm CSKH
                            </Button>
                            <Button
                                onClick={loadData}
                                icon={<ReloadOutlined />}
                                loading={loading}
                            >
                                Refresh
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            <Table
                size="small"
                dataSource={customerCareLogList}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={false}
                bordered
                locale={{
                    emptyText: <Empty description="Chưa có lịch sử CSKH" />
                }}
            />

            <CustomerCareForm
                visible={modalVisible}
                customerId={customer?.id}
                reloadData={loadData}
                editingItem={editingItem}
                onCancel={() => setModalVisible(false)}
            />
        </div>
    );
};

export default CustomerCareList;
